import type { ExperienceItem } from '@/types';

/**
 * Professional experience sourced from Muhammad_Sufyan_Malik.pdf (CV).
 * Dates and responsibilities follow the CV wording, lightly cleaned for the web.
 */
export const experience: ExperienceItem[] = [
  {
    id: 'ard-grup-team-lead',
    company: 'ARD Grup',
    role: 'Software Team Lead',
    startDate: 'August 2026',
    endDate: 'Present',
    responsibilities: [
      'Leading engineering delivery across backend, admin, and mobile workstreams on a full-stack platform (ASP.NET Core / Angular / Flutter), owning architecture decisions and code review.',
      'Containerized applications with Docker on Linux and built multi-environment Azure DevOps CI/CD pipelines (develop, staging, release, production) across several production systems.',
      'Integrating AI-assisted tools into the development workflow, including building internal AI/DevOps agents, to speed up delivery and catch issues earlier.',
      'Implemented layered (N-tier) architecture, CQRS, and the Repository pattern for maintainable, scalable systems; integrated MediatR, Elasticsearch, and RabbitMQ.',
      'Mentoring engineers and conducting code reviews to uphold coding standards and share architectural context.',
    ],
    technologies: [
      'C#',
      '.NET Core',
      'Docker',
      'Azure DevOps',
      'CI/CD',
      'CQRS',
      'MediatR',
      'Angular',
      'Flutter',
      'Elasticsearch',
      'RabbitMQ',
    ],
    summary: 'Leading delivery across a full-stack platform, from architecture through CI/CD.',
  },
  {
    id: 'ard-grup',
    company: 'ARD Grup',
    role: 'Software Engineer',
    startDate: 'December 2021',
    endDate: 'July 2026',
    responsibilities: [
      'Participated in the full software development lifecycle, including analysis, development, testing, deployment, and code enhancements.',
      'Developed and maintained scalable web applications and portals using C# and .NET Core.',
      'Implemented layered (N-tier) architecture with clear separation of concerns to improve maintainability, testability, and scalability.',
      'Used the Repository pattern to manage data access and keep persistence concerns abstracted from business logic.',
      'Implemented CQRS to separate read and write paths for more efficient data handling.',
      'Integrated MediatR to apply the mediator pattern and reduce direct coupling between application layers.',
      'Resolved complex technical issues and provided ongoing support for production systems.',
      'Conducted code reviews to uphold coding standards and strengthen collaboration.',
      'Worked with Elasticsearch for full-text search, log analysis, and real-time indexing.',
    ],
    technologies: [
      'C#',
      '.NET Core',
      'CQRS',
      'MediatR',
      'Layered Architecture',
      'RabbitMQ',
      'Dart',
      'Git',
      'Azure DevOps',
      'SignalR',
      'Angular',
      'Elasticsearch',
    ],
    summary: 'Software development and R&D across web platforms and supporting systems.',
  },
  {
    id: 'locksmith',
    company: 'Locksmith Software Technologies',
    role: 'Mobile Application Developer (Android, iOS, Flutter)',
    startDate: 'January 2021',
    endDate: 'November 2021',
    responsibilities: [
      'Participated in the full mobile development lifecycle, including analysis, development, testing, deployment, and enhancement.',
      'Built responsive mobile interfaces and improved application performance through ongoing bug fixing.',
      'Implemented location listeners to display accurate positions on Google Maps.',
      'Documented defect details during testing to support remediation.',
      'Integrated Google Play Services and Google Maps API v2.',
      'Tested applications on physical devices and Android emulators.',
      'Created reusable Flutter components with widgets.',
      'Used Dio for REST APIs and BLoC for state management.',
      'Designed user interfaces with SwiftUI.',
      'Improved application efficiency through testing and defect resolution.',
    ],
    technologies: [
      'Java',
      'Dart',
      'Flutter',
      'XML',
      'Git',
      'BLoC',
      'Provider',
      'REST',
      'Retrofit',
      'Android SDK',
      'Material Design',
      'SwiftUI',
    ],
    summary: 'Mobile development for security-oriented solutions.',
  },
  {
    id: 'islandsinmotion',
    company: 'IslandsinMotion',
    role: 'WordPress Developer & Android Developer',
    startDate: 'September 2020',
    endDate: 'December 2020',
    responsibilities: [
      'Developed WordPress websites and mobile apps, with focus on bug fixing and performance improvements.',
      'Used Java for Android apps and WordPress CMS capabilities to keep web and mobile solutions flexible.',
    ],
    technologies: ['WordPress', 'CMS', 'cPanel', 'Plugins', 'MySQL', 'Java', 'Android'],
    summary: 'Web and mobile delivery for software and medical solutions.',
  },
  {
    id: 'activesharks',
    company: 'ActiveSharks',
    role: 'Android Developer',
    startDate: 'February 2018',
    endDate: 'August 2018',
    location: 'Karachi, Pakistan',
    responsibilities: [
      'Participated in the full software development lifecycle, including analysis, development, testing, deployment, and enhancement.',
      'Developed application logic using Android Studio and the Android SDK.',
      'Collaborated with clients on user interface analysis and feedback.',
      'Delivered feasible application solutions based on client needs.',
      'Coordinated with teammates to keep delivery on schedule.',
      'Validated applications against performance and quality requirements.',
      'Optimized how apps interfaced with supporting systems for more flexible use.',
    ],
    technologies: ['Java', 'XML', 'MVP', 'REST', 'Volley', 'Responsive Design'],
    summary: 'Android development supporting software and digital marketing work.',
  },
  {
    id: 'tech-strategies',
    company: 'Tech Strategies',
    role: 'Software Developer',
    startDate: 'June 2016',
    endDate: 'October 2016',
    location: 'Islamabad, Pakistan',
    responsibilities: [
      'Built desktop applications using C#.',
      'Developed Android applications.',
      'Implemented website front-end features.',
    ],
    technologies: ['C#', 'Android', 'Front-end Development'],
    summary: 'Generalist software development across desktop, mobile, and web.',
  },
];
